import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { post } from '../lib/api'
import { currentPushState, disablePush, enablePush, pushSupported, reconcilePush, type PushState } from '../lib/push'
import { flush, remove, type OutboxItem } from '../lib/outbox'
import { formatDateTime, relativeTime } from '../lib/format'
import { useSession } from '../state/session'
import { useToday } from '../state/today'
import { useToast } from '../state/toast'
import { Button, SectionHeading, Spinner } from '../components/ui'

export default function SettingsPage() {
  const { user, settings, signOut } = useSession()
  const { state, pending, refresh } = useToday()
  const toast = useToast()
  const navigate = useNavigate()

  const [pushState, setPushState] = useState<PushState | null>(null)
  const [pushBusy, setPushBusy] = useState(false)
  const [theme, setTheme] = useState<'system' | 'light' | 'dark'>(
    () => (localStorage.getItem('wm-theme') as 'light' | 'dark' | null) ?? 'system',
  )

  useEffect(() => {
    void reconcilePush().finally(() => void currentPushState().then(setPushState))
  }, [])

  const applyTheme = (next: 'system' | 'light' | 'dark') => {
    setTheme(next)
    if (next === 'system') {
      localStorage.removeItem('wm-theme')
      delete document.documentElement.dataset.theme
    } else {
      localStorage.setItem('wm-theme', next)
      document.documentElement.dataset.theme = next
    }
  }

  const toggleNotifications = async () => {
    setPushBusy(true)
    try {
      if (pushState === 'subscribed') {
        await disablePush()
        setPushState('prompt')
        toast.show('Reminders turned off on this device')
      } else {
        const result = await enablePush()
        setPushState(result.state)
        if (result.state === 'subscribed') toast.success('Reminders are on for this device')
        else if (result.message) toast.warn(result.message)
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not change notifications')
    } finally {
      setPushBusy(false)
    }
  }

  const sendTest = async () => {
    try {
      await post('/api/push/test')
      toast.success('Test sent. It should arrive in a second.')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Test failed')
    }
  }

  if (!user || !state) return <div className="py-16"><Spinner /></div>

  return (
    <div className="space-y-7">
      <header>
        <p className="label">Signed in as</p>
        <h1 className="mt-0.5 text-2xl">{user.name}</h1>
        <p className="text-sm text-graphite-2">{user.email}</p>
      </header>

      <section>
        <SectionHeading>Reminders on this device</SectionHeading>
        <div className="card space-y-3 p-4">
          <PushStatusLine state={pushState} configured={settings.pushConfigured} />

          <div className="flex flex-wrap gap-2">
            <Button
              variant={pushState === 'subscribed' ? 'secondary' : 'primary'}
              loading={pushBusy}
              disabled={!pushSupported() || !settings.pushConfigured}
              onClick={() => void toggleNotifications()}
            >
              {pushState === 'subscribed' ? 'Turn off on this device' : 'Turn on reminders'}
            </Button>
            {pushState === 'subscribed' && (
              <Button onClick={() => void sendTest()}>Send a test</Button>
            )}
          </div>

          {!pushSupported() && (
            <p className="text-xs text-graphite-2">
              On iPhone, reminders only work once the app is added to the Home Screen. Tap Share,
              then Add to Home Screen, then open it from there.
            </p>
          )}
        </div>
      </section>

      <section>
        <SectionHeading>Location</SectionHeading>
        <p className="text-sm text-graphite-2">
          The app only asks for your location when you clock in, post an update or pick a site.
          If your iPhone still asks every time, open <strong>Settings → Privacy &amp; Security →
          Location Services → Safari Websites</strong> and choose <strong>While Using the App</strong>.
        </p>
      </section>

      <section>
        <SectionHeading>Your schedule</SectionHeading>
        {state.prompts.length === 0 ? (
          <p className="text-sm text-graphite-2">
            The office has not assigned you any reminders yet.
          </p>
        ) : (
          <div className="space-y-1.5">
            {state.prompts.map((prompt) => (
              <div
                key={prompt.scheduleId}
                className="flex items-center justify-between gap-3 border-b border-line pb-1.5"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-bold">{prompt.label}</p>
                  <p className="text-xs text-graphite-2">
                    {prompt.action === 'clock_in' ? 'Clock in'
                      : prompt.action === 'clock_out' ? 'Clock out'
                        : 'Progress update'}
                  </p>
                </div>
                <span className="font-mono text-lg font-semibold tnum">{prompt.localTime}</span>
              </div>
            ))}
          </div>
        )}
        <p className="mt-2 text-xs text-graphite-2">
          Times are set by the office in {state.timezone}.
        </p>
      </section>

      {pending.length > 0 && (
        <section>
          <SectionHeading
            action={
              <button
                type="button"
                className="link text-xs"
                onClick={() => void flush().then(() => refresh())}
              >
                Retry now
              </button>
            }
          >
            Waiting to send ({pending.length})
          </SectionHeading>
          <div className="space-y-2">
            {pending.map((item) => (
              <PendingRow key={item.id} item={item} onDrop={() => void remove(item.id)} />
            ))}
          </div>
        </section>
      )}

      <section>
        <SectionHeading>Appearance</SectionHeading>
        <div className="flex gap-1">
          {(['system', 'light', 'dark'] as const).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => applyTheme(option)}
              className={`btn flex-1 capitalize ${theme === option ? 'btn-primary' : 'btn-secondary'}`}
            >
              {option}
            </button>
          ))}
        </div>
      </section>

      <section className="space-y-2">
        <SectionHeading>Account</SectionHeading>
        <Button className="w-full" onClick={() => navigate('/set-password')}>
          Change password
        </Button>
        <Button
          variant="danger"
          className="w-full"
          onClick={() => void signOut().then(() => navigate('/login', { replace: true }))}
        >
          Sign out
        </Button>
      </section>

      <p className="pb-4 text-center font-mono text-[10px] tracking-widest text-graphite-2 uppercase">
        {settings.orgName} · Member since {formatDateTime(user.createdAt, state.timezone)}
      </p>
    </div>
  )
}

function PushStatusLine({ state, configured }: { state: PushState | null; configured: boolean }) {
  if (!configured) {
    return (
      <p className="text-sm">
        <strong>Not available yet.</strong> Push keys have not been set up on this deployment.
      </p>
    )
  }
  switch (state) {
    case 'subscribed':
      return <p className="text-sm"><strong className="text-ok">On.</strong> You will get your scheduled reminders here.</p>
    case 'denied':
      return (
        <p className="text-sm">
          <strong className="text-alarm">Blocked.</strong> Notifications are turned off for this
          site in your browser settings. Allow them there, then come back.
        </p>
      )
    case 'unsupported':
      return <p className="text-sm"><strong>This browser cannot receive reminders.</strong></p>
    case 'no-service-worker':
      return (
        <p className="text-sm">
          <strong>Not available in this build.</strong> Reminders need the service worker, which
          only ships in the deployed app.
        </p>
      )
    case null:
      return <p className="text-sm text-graphite-2">Checking…</p>
    default:
      return <p className="text-sm">Reminders are off on this device.</p>
  }
}

function PendingRow({ item, onDrop }: { item: OutboxItem; onDrop: () => void }) {
  return (
    <div className="card flex items-center justify-between gap-3 p-3">
      <div className="min-w-0">
        <p className="truncate text-sm font-bold">{item.summary}</p>
        <p className="text-xs text-graphite-2">
          Queued {relativeTime(item.createdAt)}
          {item.attempts > 0 && ` · ${item.attempts} attempt${item.attempts > 1 ? 's' : ''}`}
          {item.lastError && ` · ${item.lastError}`}
        </p>
      </div>
      <button
        type="button"
        onClick={onDrop}
        className="shrink-0 text-xs font-bold text-alarm underline underline-offset-2"
      >
        Discard
      </button>
    </div>
  )
}
